import { FC } from "react";
import { Box, Text, Flex } from "@radix-ui/themes";
import styled from "@emotion/styled";
import { SidebarItemRow } from "@/app/layout/sidebar-item-row";
import { SidebarGroup } from "@/app/layout/sidebar-group";
import {
  SidebarGroupId,
  SidebarItemKind,
  SidebarSectionItem,
  SidebarSubItem,
} from "@/app/layout/types";
import { controlFlowModule } from "@/modules/control-flow";
import { ChevronDown, ChevronRight } from "lucide-react";
import { SIDEBAR_SECTION_HEADER_HEIGHT } from "@/app/layout/constants";
import { BaseButton } from "@/components/button";

interface SidebarSectionProps {
  section: SidebarSectionItem;
  isCollapsed: boolean;
  onToggle: () => void;
  onItemSelect?: () => void;
  isGroupCollapsed?: (groupId: SidebarGroupId) => boolean;
  onGroupToggle?: (groupId: SidebarGroupId) => void;
}

export const SidebarSection: FC<SidebarSectionProps> = ({
  section,
  isCollapsed,
  onToggle,
  onItemSelect,
  isGroupCollapsed,
  onGroupToggle,
}) => {
  const renderItem = (item: SidebarSubItem, _index: number) => {
    switch (item.kind) {
      case SidebarItemKind.Button:
        return (
          <SidebarItemRow key={`${section.id}-${item.href}`} item={item} onSelect={onItemSelect} />
        );
      case SidebarItemKind.Group: {
        const collapsed = isGroupCollapsed?.(item.id) ?? false;
        return (
          <SidebarGroup
            key={item.id}
            group={item}
            isCollapsed={collapsed}
            onToggle={() => onGroupToggle?.(item.id)}
            onItemSelect={onItemSelect}
          />
        );
      }
      default:
        controlFlowModule.assertUnreachableCase({
          message: "Failed to match sidebar subsection item kind.",
          value: item,
        });
        return null;
    }
  };

  return (
    <SectionBox>
      <SectionHeader onClick={onToggle}>
        <Flex align="center" gap="1">
          <SectionLabel>{section.label}</SectionLabel>
          {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
        </Flex>
      </SectionHeader>
      {!isCollapsed && (
        <SubSectionBox>{section.items.map((item, index) => renderItem(item, index))}</SubSectionBox>
      )}
    </SectionBox>
  );
};

const SectionBox = styled.div`
  display: flex;
  flex-direction: column;
  user-select: none;
  gap: 1px;
`;

const SectionHeader = styled(BaseButton)`
  cursor: pointer;
  padding-left: 16px !important;
  padding-right: 16px !important;
  color: var(--gray-12);
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border: none !important;
  box-shadow: none !important;
  height: ${SIDEBAR_SECTION_HEADER_HEIGHT}px;
  min-height: ${SIDEBAR_SECTION_HEADER_HEIGHT}px;
  margin: 0px !important;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  padding-left: 6px !important;
  padding-right: 0px !important;
  background-color: transparent;
  border-radius: 4px !important;

  &:hover {
    background-color: var(--gray-3) !important;
  }
`;

const SectionLabel = styled(Text)`
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  color: var(--gray-12);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubSectionBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;
